import { AlertDefaults, AlertType, Field, PriorityType } from 'src/app/hickory-api';
import { IncidentModelHelper } from 'src/app/shared/incident';
import { AlertModelService } from '../alert-model.service';
import { AlertChannelsHelper } from './alert-channels-helper';
import { AlertPollHelper } from './alert-poll-helper';
import { AlertRecipientsHelper } from './alert-recipients-helper';
import { AlertReporterHelper } from './alert-reporter-helper';
import { AlertScheduleHelper } from './alert-schedule-helper';
import { AlertSenderHelper } from './alert-sender-helper';
import { AlertSeverityHelper } from './alert-severity-helper';
import { AlertSurveyHelper } from './alert-survey-helper';
import { AlertAttachmentsHelper } from './alert-attachments-helper';
import { AlertMessageHelper } from './alert-message-helper';
import { LogHelper } from 'src/app/shared/logging/log-helper';
import { deepCopy, simpleSort } from 'src/app/shared/model/utility';
import { AlertSocialHelper } from './alert-social-helper';
import { AlertFacilitiesHelper } from './alert-facilities-helper';

export class AlertFields {
  static readonly ALL_FIELDS = 'allFields';

  static readonly ALERT_ACK = 'alertAck';
  static readonly ASSET_LIST_IMPORT = 'assetListImport';
  static readonly ASSET_SELECTION = 'assetSelection';
  static readonly ASSOCIATION_RECIPIENTS = 'associationRecipients';
  static readonly ATTACHMENTS = 'attachments';
  static readonly CHANNELS = 'channels';
  static readonly FACILITIES = 'advancedSiteSelection';
  static readonly GEO_FENCING = 'geoFencing'
  static readonly INCIDENT_V1 = 'incident';
  static readonly INCIDENT_V2 = 'incident-v2';
  static readonly HIDE_ATTACHMENTS = 'hideAttachments';
  static readonly LOCATION = 'location';
  static readonly MESSAGE = 'message';
  static readonly MESSAGE_PERSONALIZATION = 'messagePersonalization';
  static readonly POLL_OPTIONS = 'pollOptions';
  static readonly POLL_QUESTION = 'pollQuestion';
  static readonly PRIORITY = 'priority';
  static readonly PUBLIC_ATTACHMENTS = 'publicAttachments';
  static readonly EXPIRATION = 'expiration';
  static readonly RECIPIENTS = 'recipients';
  static readonly REPORTER = 'reporter';
  static readonly SCHEDULE = 'schedule';
  static readonly SENDER = 'sender';
  static readonly SEVERITY_ASSESSMENT = 'severityAssessment';
  static readonly SURVEY_SENDER = 'surveySender';
  static readonly SURVEY_RECIPIENT = 'surveyRecipient';
  static readonly SOCIAL = 'social';
  static readonly SUBJECT = 'subject';
  static readonly TEMPLATE = 'template';
  static readonly BRAND = 'brand';
  static readonly DISABLE_REPLY_ALL = 'disableReplyAll';
  
  // psuedo fields
  static readonly REQUIRED = "required";
  static readonly WORKFLOW = "workflow";
}

const tag = 'Alert.Fields';

export class AlertFieldsHelper {
  static postProcess(service: AlertModelService, alert: AlertDefaults = service.alertSettings, fields: Field[] = alert.fields, trigger?: string) {
    if (!fields) return;

    // Important - the `fields` array must be modified in place (ROAD-12260)
    //   Allowed: fields[index].someProperty = someValue
    //   Allowed: fields.splice()
    //   Allowed: fields.push()
    //   Not Allowed: fields = fields.filter()
    //   Not Allowed: fields = someOtherFields

    LogHelper.debug(tag, 'postProcess', trigger, fields.map(x => x.name).join());

    // ROAD-11316: When we're editing a template and we're updating defaults b/c the priority
    // has changed, we need to make sure that we preserve the workingCopy's fields in case the
    // user has manually added/removed fields.
    if (service.isEditingTemplate && service.alertSettings.fields?.length && [AlertFields.INCIDENT_V1, AlertFields.INCIDENT_V2, AlertFields.PRIORITY].includes(trigger)) {
      const oldFields = service.alertSettings.fields;
      const oldFieldNames = simpleSort(oldFields.map(f => f.name)).join();
      const newFieldNames = simpleSort(fields.map(f => f.name)).join();
      if (oldFieldNames !== newFieldNames) {
        LogHelper.debug(tag, 'preserving existing template field selections: ', oldFields.map(f => f.name).join());
        // replace the contents of `fields` with the contents from `oldFields`
        fields.splice(0, fields.length, ...deepCopy(oldFields));
      }
    }

    // make sure that required alert classification fields are applied
    if (service.useAlertPriorityV2) {
      this.applyRequiredfields(fields);
    }

    // sort fields based on business rules (combined message, alert classification, etc)
    this.sortTemplateFieldsWithService(service, fields);

    this.prepareAssetSiteSelection(fields);
    this.prepareAttachments(service, fields);

    fields.forEach(f => {
      f.required = f.required || this.fieldsAlwaysRequired(alert.alertType, service.useAlertPriorityV2).includes(f.name);
    });

    // if the incident-v1 field is not present, remove the data
    if (!fields.find(f => f.name === AlertFields.INCIDENT_V1)) {
      alert.message.incidentId = undefined;
      alert.message.incident = undefined;
    }

    // if the incident-v2 field is not present, remove the data
    if (!fields.find(f => f.name === AlertFields.INCIDENT_V2)) {
      alert.message.incidentEvent = undefined;
    }

    // Clear severity assessment data if field is not present
    if (!fields.find(f => f.name === AlertFields.SEVERITY_ASSESSMENT)) {
      alert.message.severityAssessment = undefined;
    }

    // Clear sender survey data if field is not present
    if (!fields.find(f => f.name === AlertFields.SURVEY_SENDER) && !!alert.message.survey) {
      alert.message.survey.sender = undefined;
    }

    // Clear recipient survey data if field is not present
    if (!fields.find(f => f.name === AlertFields.SURVEY_RECIPIENT) && !!alert.message.survey) {
      alert.message.survey.recipient = undefined;
    }

    if (!fields.find(f => f.name === AlertFields.FACILITIES)) {
      if (alert.location) {
        alert.location.facilities = undefined;
      }

      alert.facilities = {};
    }

    // these cases have special rules for alert fields, so don't replace them here
    if (!service.isPanicTemplate && !service.isEditingTemplate && !service.isCallCenter) {
      alert.fields = fields;
    }
  }

  private static prepareAssetSiteSelection(fields: Field[]) {
    // hack - Asset Site Selection - consolidate to a single field (ASSOCIATION_RECIPIENTS)
    let listImportIndex = fields.findIndex(f => f.name === AlertFields.ASSET_LIST_IMPORT);
    let selectionIndex = fields.findIndex(f => f.name === AlertFields.ASSET_SELECTION);
    const siteSelectionField = fields.find(f => f.name === AlertFields.ASSOCIATION_RECIPIENTS);
    if (!siteSelectionField && (listImportIndex > -1 || selectionIndex > -1)) {
      // place before the earliest field
      if (listImportIndex === -1) listImportIndex = Number.MAX_SAFE_INTEGER;
      if (selectionIndex === -1) selectionIndex = Number.MAX_SAFE_INTEGER;
      const i = (listImportIndex < selectionIndex) ? listImportIndex : selectionIndex;
      fields.splice(i, 0, { name: AlertFields.ASSOCIATION_RECIPIENTS });
    }
  }

  private static prepareAttachments(service: AlertModelService, fields: Field[]) {
    // hack - special handling for 'attachments' fields
    if (!service.isEditingTemplate && !service.isCallCenter) { // we don't do this for the template form?
      // remove 'attachments' if 'hideAttachments' is present
      // else if 'publicAttachments' is present, leave it alone
      // else add 'attachments' if it's not already there
      if (fields.find(f => f.name === AlertFields.HIDE_ATTACHMENTS)) {
        this.removeField(fields, AlertFields.ATTACHMENTS);
        this.removeField(fields, AlertFields.PUBLIC_ATTACHMENTS);
      } else if (fields.find(f => f.name === AlertFields.PUBLIC_ATTACHMENTS)) {
        // no-op
      } else if (!fields.find(f => f.name === AlertFields.ATTACHMENTS)) {
        fields.push({ name: AlertFields.ATTACHMENTS, required: false });
      }
    }

    return fields;
  }

  /**
   * Re-sorts the fields array depending on certain feature toggles, so that certain components are
   * grouped together in logical ways (message, recipients)
   */
  static sortTemplateFieldsWithService(service: AlertModelService, fields: Field[]) {
    this.sortTemplateFields(service.alertType, fields, service.useRecipientsV2, service.useAlertPriorityV2)
  }

  static sortTemplateFields(formType: AlertType, fields: Field[], useRecipientsV2: boolean, useAlertPriorityV2: boolean) {
    if (!fields) return;

    this.sortFieldsForCombinedMessage(formType, fields);
    if (useRecipientsV2) this.sortFieldsForRecipientsV2(fields);
    if (useAlertPriorityV2) this.sortFieldsForAlertPriorityV2(fields);
  }

  private static sortFieldsForCombinedMessage(formType: AlertType, fields: Field[]) {
    if (formType === AlertType.Poll) {
      this.sortPollFieldsForCombinedMessage(fields);
    } else {
      this.sortAlertFieldsForCombinedMessage(fields);
    }
  }

  private static sortAlertFieldsForCombinedMessage(fields: Field[]) {
    let moveIndex = -1;
    if (!fields) return;

    // don't sort if we don't have message in our list
    if (fields.findIndex(f => f.name === AlertFields.MESSAGE) === -1) return;

    moveIndex = fields.findIndex(f => f.name === AlertFields.SOCIAL);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      const reference = fields.findIndex(f => f.name === AlertFields.MESSAGE);
      fields.splice(reference + 1, 0, field[0]);
    }

    moveIndex = fields.findIndex(f => f.name === AlertFields.CHANNELS);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      const reference = fields.findIndex(f => f.name === AlertFields.MESSAGE);
      fields.splice(reference + 1, 0, field[0]);
    }

    moveIndex = fields.findIndex(f => f.name === AlertFields.PRIORITY);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      const reference = fields.findIndex(f => f.name === AlertFields.MESSAGE);
      fields.splice(reference, 0, field[0]);
    }

    moveIndex = fields.findIndex(f => f.name === AlertFields.SUBJECT);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      const reference = fields.findIndex(f => f.name === AlertFields.MESSAGE);
      fields.splice(reference, 0, field[0]);
    }
  }

  private static sortPollFieldsForCombinedMessage(fields: Field[]) {
    let moveIndex = -1;
    if (!fields) return;

    // don't sort if we don't have pollQuestion in our list
    if (fields.findIndex(f => f.name === AlertFields.POLL_QUESTION) === -1) return;

    moveIndex = fields.findIndex(f => f.name === AlertFields.CHANNELS);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      const reference = fields.findIndex(f => f.name === AlertFields.POLL_QUESTION);
      fields.splice(reference + 1, 0, field[0]);
    }

    moveIndex = fields.findIndex(f => f.name === AlertFields.POLL_OPTIONS);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      const reference = fields.findIndex(f => f.name === AlertFields.POLL_QUESTION);
      fields.splice(reference + 1, 0, field[0]);
    }

    moveIndex = fields.findIndex(f => f.name === AlertFields.PRIORITY);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      const reference = fields.findIndex(f => f.name === AlertFields.POLL_QUESTION);
      fields.splice(reference, 0, field[0]);
    }

    moveIndex = fields.findIndex(f => f.name === AlertFields.SUBJECT);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      const reference = fields.findIndex(f => f.name === AlertFields.POLL_QUESTION);
      fields.splice(reference, 0, field[0]);
    }
  }

  private static sortFieldsForRecipientsV2(fields: Field[]) {
    // don't sort if we don't have recipients in our list
    if (!fields || fields.findIndex(f => f.name === AlertFields.RECIPIENTS) === -1) return;
    const moveIndex = fields.findIndex(f => f.name === AlertFields.GEO_FENCING);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      const reference = fields.findIndex(f => f.name === AlertFields.RECIPIENTS);
      fields.splice(reference + 1, 0, field[0]);
    }
  }

  private static sortFieldsForAlertPriorityV2(fields: Field[]) {
    let moveIndex = -1;
    let reference = -1;
    if (!fields) return;

    moveIndex = fields.findIndex(f => f.name === AlertFields.TEMPLATE);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      fields.splice(++reference, 0, field[0]);
    }

    moveIndex = fields.findIndex(f => f.name === AlertFields.INCIDENT_V1);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      fields.splice(++reference, 0, field[0]);
    }

    moveIndex = fields.findIndex(f => f.name === AlertFields.INCIDENT_V2);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      fields.splice(++reference, 0, field[0]);
    }

    moveIndex = fields.findIndex(f => f.name === AlertFields.PRIORITY);
    if (moveIndex !== -1) {
      const field = fields.splice(moveIndex, 1);
      fields.splice(++reference, 0, field[0]);
    }
  }

  static isMissingAlertPriorityV2Field(fields: Field[]): boolean {
    if (!fields) return true;
    const names = fields.map(f => f.name);
    // when alert priority classification is enforced, one of these fields is required:
    return !names.includes(AlertFields.INCIDENT_V1) && !names.includes(AlertFields.INCIDENT_V2) && !names.includes(AlertFields.PRIORITY);
  }

  static applyRequiredfields(fields: Field[]) {
    if (!fields) return;

    // #Rule: one of the following fields must exist and must be required:
    //     `incident-v2`, `incident`, `priority`
    // Precedence should also be enforced: incident-v2 > incident > priority

    // if we have the `incident-v2` field, set it to required=true, remove competing fields, early exit
    let field = fields.find(f => f.name === AlertFields.INCIDENT_V2);
    if (field) {
      field.required = true;
      this.removeField(fields, AlertFields.INCIDENT_V1);
      this.removeField(fields, AlertFields.PRIORITY);
      return;
    }

    // if we have the `incident` field, set it to required=true, remove competing field, early exit
    field = fields.find(f => f.name === AlertFields.INCIDENT_V1);
    if (field) {
      field.required = true;
      this.removeField(fields, AlertFields.PRIORITY);
      return;
    }

    // if we have the `priority` field, set it to required=true, early exit
    field = fields.find(f => f.name === AlertFields.PRIORITY);
    if (field) {
      field.required = true;
      return;
    }

    // we didn't find any existing, so add the `priority` field and set it to required=true
    fields?.push({name: AlertFields.PRIORITY, required: true });
  }

  static shouldDisplayPriorityFieldWithMessage(service: AlertModelService): boolean {
    if (service.useAlertPriorityV2) return false; // priority gets its own card in this case
    if (this.hasIncidentField(service)) return false;
    const field = service.findField(AlertFields.PRIORITY);
    return !!field && (service.isEditingTemplate || !field.invisible);
  }

  static hasIncidentField(service: AlertModelService): boolean {
    return !!service.findField(AlertFields.INCIDENT_V1) || !!service.findField(AlertFields.INCIDENT_V2)
  }

  static canDragFieldInFormWithService(service: AlertModelService, field: Field): boolean {
    return this.canDragFieldInForm(service.alertType, field, service.useRecipientsV2, service.useAlertPriorityV2)
  }

  static canDragFieldInForm(formType: AlertType, field: Field, useRecipientsV2: boolean, useAlertPriorityV2): boolean {
    let canDrag = true;
    if (formType === AlertType.Poll) {
      canDrag = canDrag && (![AlertFields.SUBJECT, AlertFields.CHANNELS, AlertFields.POLL_OPTIONS, AlertFields.PRIORITY].includes(field.name));
    } else {
      canDrag = canDrag && (![AlertFields.SUBJECT, AlertFields.CHANNELS, AlertFields.SOCIAL, AlertFields.PRIORITY].includes(field.name));
    }
    if (useRecipientsV2) {
      canDrag = canDrag && (![AlertFields.GEO_FENCING].includes(field.name));
    }
    if (useAlertPriorityV2) {
      canDrag = canDrag && (![AlertFields.INCIDENT_V1, AlertFields.INCIDENT_V2, AlertFields.PRIORITY, AlertFields.TEMPLATE, AlertFields.REQUIRED].includes(field.name));
    }

    return canDrag;
  }

  // when Alert Priority v2 is enabled, we need to show one and only one of these fields at the top of the form
  static fieldRequiredAlias(service: AlertModelService): string {
    return service.findField(AlertFields.INCIDENT_V1)?.name ?? service.findField(AlertFields.INCIDENT_V2)?.name ?? AlertFields.PRIORITY;
  }

  static fieldRequiredTitle(service: AlertModelService): string {
    const field = this.fieldRequiredAlias(service);
    switch (field) {
      case AlertFields.INCIDENT_V1: return 'Incident';
      case AlertFields.INCIDENT_V2: return 'Incident Event';
      case AlertFields.PRIORITY: return 'Priority';
      default: return 'Selection Required';
    }
  }

  static isComplete(service: AlertModelService, fieldName: string): boolean {
    switch (fieldName) {
      case AlertFields.ATTACHMENTS: return !!service.selectedFiles?.length;
      case AlertFields.CHANNELS: return !AlertChannelsHelper.validate(service)?.length;
      case AlertFields.FACILITIES: return !AlertFacilitiesHelper.validate(service)?.length;
      case AlertFields.GEO_FENCING: return !!service.alertRecipients?.geofences && !!service.alertRecipients?.featureLayers?.length;
      case AlertFields.INCIDENT_V1: return !!service.alertIncident?.id || !!service.alertMessage?.incidentId;
      case AlertFields.INCIDENT_V2: return !!service.alertIncidentEvent;
      case AlertFields.LOCATION: return service.mapLocation?.isReady && service.mapLocation?.confirmed;
      case AlertFields.MESSAGE: return !AlertMessageHelper.validate(service)?.length;
      case AlertFields.POLL_OPTIONS: return !AlertPollHelper.validateOptions(service)?.length;
      case AlertFields.POLL_QUESTION: return !AlertPollHelper.validateQuestion(service)?.length && !AlertPollHelper.validateLength(service)?.length;
      case AlertFields.PRIORITY: return !!service.alertPriority;
      case AlertFields.PUBLIC_ATTACHMENTS: return !!service.alertMessage?.files?.length;
      case AlertFields.EXPIRATION: return !!service.alertSettings.schedule?.duration;
      case AlertFields.REQUIRED: return this.isComplete(service, this.fieldRequiredAlias(service))
      case AlertFields.RECIPIENTS: return !AlertRecipientsHelper.validate(service)?.length;
      case AlertFields.REPORTER: return !!service.callCenterReporter?.id;
      case AlertFields.SCHEDULE: return !AlertScheduleHelper.validate(service)?.length;
      case AlertFields.SENDER: return !!service.alertSender?.displayName?.length && !!service.alertSender?.email?.length;
      case AlertFields.SEVERITY_ASSESSMENT: return !AlertSeverityHelper.validate(service)?.length;
      case AlertFields.SURVEY_SENDER: return !AlertSurveyHelper.validateSender(service)?.length;
      case AlertFields.SURVEY_RECIPIENT: return !AlertSurveyHelper.validateRecipient(service)?.length;
      case AlertFields.SOCIAL: return !AlertSocialHelper.validateSocial(service)?.length;
      case AlertFields.SUBJECT: return !!service.alertSettings.message?.basic?.subject;
      case AlertFields.TEMPLATE: return !!service.alertTemplate.id;
      default: return true;
    }
  }

  static summaryText(service: AlertModelService, fieldName: string): string {
    switch (fieldName) {
      case AlertFields.ATTACHMENTS: return AlertAttachmentsHelper.summaryText(service, false);
      case AlertFields.CHANNELS: return null;
      case AlertFields.FACILITIES: return AlertFacilitiesHelper.summaryText(service);
      case AlertFields.GEO_FENCING: return 'Complete';
      case AlertFields.INCIDENT_V1: return IncidentModelHelper.getIncidentName(service.alertIncident, null);
      case AlertFields.INCIDENT_V2: return IncidentModelHelper.getIncidentName(null, service.alertIncidentEvent);
      case AlertFields.LOCATION: return service.mapLocation?.confirmed ? service.mapLocation?.textAddress : null;
      case AlertFields.MESSAGE: return 'Complete';
      case AlertFields.POLL_OPTIONS: return 'Complete';
      case AlertFields.POLL_QUESTION: return 'Complete';
      case AlertFields.PRIORITY: return service.alertPriority === PriorityType.NonEmergency ? 'Non-emergency' : 'Emergency';
      case AlertFields.PUBLIC_ATTACHMENTS: return AlertAttachmentsHelper.summaryText(service, true);
      case AlertFields.EXPIRATION: return 'Complete';
      case AlertFields.REQUIRED: return this.summaryText(service, this.fieldRequiredAlias(service))
      case AlertFields.RECIPIENTS: return AlertRecipientsHelper.summaryText(service);
      case AlertFields.REPORTER: return AlertReporterHelper.summaryText(service);
      case AlertFields.SCHEDULE: return null;
      case AlertFields.SENDER: return AlertSenderHelper.summaryText(service);
      case AlertFields.SEVERITY_ASSESSMENT: return 'Complete';
      case AlertFields.SURVEY_SENDER: return 'Complete';
      case AlertFields.SURVEY_RECIPIENT: return 'Complete';
      case AlertFields.SOCIAL: return AlertSocialHelper.socialSummaryText(service.alertChannels);
      case AlertFields.SUBJECT: return 'Complete';
      case AlertFields.TEMPLATE: return service.alertTemplate?.name;
      case AlertFields.DISABLE_REPLY_ALL: return this.advancedSettingsSummary(service);
      default: return null;
    }
  }

  static requiredFields(service: AlertModelService): Field[] {
    let fields = service.alertFields.filter(f => f.required);

    if (service.useAlertPriorityV2) {
      const alias = AlertFieldsHelper.fieldRequiredAlias(service)
      fields = fields.filter(f => f.name !== alias);
      fields.splice(0, 0, { name: alias, required: true, });
    }
    if (fields.length) return fields;
    else if (service.isCallCenter) return this.callCenterFields().filter(f => f.name === AlertFields.REPORTER);
    else return [];
  }

  static fieldsRequiredByDefaultWithService(service: AlertModelService): string[] {
    return this.fieldsRequiredByDefaultOnCreate(service.alertType, service.useAlertPriorityV2);
  }

  // for these fields, we disable the `required` checkbox on the form editor - they are always marked as required
  static fieldsAlwaysRequired(type: AlertType, useAlertPriorityV2: boolean): string[] {
    const fields = useAlertPriorityV2 ? [AlertFields.INCIDENT_V1, AlertFields.INCIDENT_V2, AlertFields.PRIORITY] : [];
    if (type === AlertType.Poll) {
      fields.push(AlertFields.RECIPIENTS, AlertFields.SUBJECT, AlertFields.POLL_QUESTION, AlertFields.POLL_OPTIONS, AlertFields.CHANNELS);
    } else {
      fields.push(AlertFields.RECIPIENTS, AlertFields.SUBJECT, AlertFields.MESSAGE, AlertFields.CHANNELS);
    }
    return fields;
  }

  // for these fields, we set required=true by default (but still editable unless in the `fieldsAlwaysRequired` list)
  static fieldsRequiredByDefaultOnCreate(type: AlertType, useAlertPriorityV2: boolean): string[] {
    const fields = this.fieldsAlwaysRequired(type, useAlertPriorityV2);
    if (type === AlertType.Poll) {
      fields.push(AlertFields.SEVERITY_ASSESSMENT);
    } else {
      fields.push(AlertFields.SEVERITY_ASSESSMENT, AlertFields.SURVEY_SENDER);
    }
    return fields;
  }

  static fieldsRequiredByDefaultOnEdit(type: AlertType, useAlertPriorityV2: boolean): string[] {
    // if the form was saved with anything else as optional, let's respect that
    return this.fieldsAlwaysRequired(type, useAlertPriorityV2);
  }

  static callCenterFields(): Field[] {
    return [
      { name: AlertFields.REPORTER, required: true, readonly: false, invisible: false },
      { name: AlertFields.LOCATION, required: true, readonly: false, invisible: false },
      { name: AlertFields.INCIDENT_V1, required: true, readonly: false, invisible: false },
      { name: AlertFields.SEVERITY_ASSESSMENT, required: true, readonly: false, invisible: false },
      { name: AlertFields.RECIPIENTS, required: true, readonly: false, invisible: false },
      { name: AlertFields.SUBJECT, required: true, readonly: false, invisible: false },
      { name: AlertFields.MESSAGE, required: true, readonly: false, invisible: false },
    ];
  }

  static hasAdvancedSettingsFields(fields: Field[]): boolean {
    return fields?.some(f => this.isAdvancedSettingsField(f));
  }

  static isAdvancedSettingsField(field: Field): boolean {
    const fields = [
      AlertFields.DISABLE_REPLY_ALL
    ];
    return fields.some(c => c === field?.name);
  }

  static advancedSettingsSummary(service: AlertModelService): string {
    return service.alertSettings?.relatedAlertActions?.disableReplyAll ? 'Reply All is Disabled' : null;
  }

  // modifies the fields array in place
  // removes any fields where name === `toRemove`
  static removeField(fields: Field[], toRemove: string) {
    if (!fields?.length || !toRemove) return;
    while (fields.length > 0) {
      const index = fields.findIndex(item => item.name === toRemove);
      if (index === -1) break;
      fields.splice(index, 1);
    }
  }
}
